import { pipe } from 'fp-ts/function';
import { sort } from 'fp-ts/Array';
import { camelCase } from 'lodash';
import { ord, string } from 'fp-ts';
import { MessageMedium } from 'constants/messageMediums';
import {
  ConfigByMedium,
  ConfigByMediumSerialized,
  FrequencyManagementMessageType,
  SupportedMediums,
  FrequencyMode,
} from 'modules/frequencyManagement/lib/types';

export const supportedMediums: Array<SupportedMediums> = [
  MessageMedium.Email,
  MessageMedium.SMS,
  MessageMedium.Push,
];

/** Parse received data and ensure data values present */
export const parseFrequencyManagementResponse = (
  frequencyData: ConfigByMediumSerialized,
): ConfigByMedium => {
  return supportedMediums.reduce((accConfig, currentMedium) => {
    const { isConfiguredForSending, settings, messageTypes } =
      frequencyData.currentProject.settings.frequencyManagement[currentMedium];
    return {
      ...accConfig,
      [currentMedium]: {
        isConfiguredForSending,
        settings: {
          ...(settings || {}),
          mode: settings?.mode || FrequencyMode.Off,
        },
        messageTypes,
      },
    };
  }, {} as ConfigByMedium);
};

export const lowerCaseMessageMedium = (messageMedium: MessageMedium) => {
  if (messageMedium === MessageMedium.SMS) return 'SMS';
  return messageMedium.toLowerCase();
};

/**
 * Message types are sorted first by channel then by name. Because message type
 * names are not required to be unique we must append the id to ensure consistency
 * in ordering. (Although message type names can be updated, for any given visit
 * to the Frequency Management page, the ordering of message types should be
 * consistent.)
 */
const nameAndId = (messageType: FrequencyManagementMessageType) => {
  const {
    name,
    id,
    messageChannel: { name: channelName },
  } = messageType;
  return `${camelCase(channelName)}_${camelCase(name)}_${id}`;
};

const byNameAndId = pipe(string.Ord, ord.contramap(nameAndId));

export const sortMessageTypes = sort(byNameAndId);
